export interface FrontendLogEvent {
    level?: string;
    category?: string;
    eventName?: string;
    componentOrWidget?: string;
    path?: string;
    url?: string;
    sessionId?: string;
    requestId?: string;
    data?: unknown;
}

export interface FrontendLoggerApi {
    logEvent: (event: FrontendLogEvent) => void;
    logError: (event: FrontendLogEvent) => void;
    flush: () => Promise<void>;
}

type BrowserRuntime = typeof globalThis & Window;

const batchEndpoint = "/api/frontend-log/batch";
const flushIntervalMs = 3000;
const maxBatchSize = 10;

const queue: FrontendLogEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let hasBoundLifecycleEvents = false;

function getBrowserRuntime(): BrowserRuntime | null {
    if (typeof globalThis === "undefined") {
        return null;
    }

    const runtime = globalThis as typeof globalThis & { window?: Window; document?: Document };
    if (typeof runtime.window === "undefined" || runtime.window !== runtime || typeof runtime.document === "undefined") {
        return null;
    }

    return runtime as BrowserRuntime;
}

function getDefaultPath(runtime: BrowserRuntime): string | undefined {
    if (!runtime.location) {
        return undefined;
    }

    return `${runtime.location.pathname}${runtime.location.search}`;
}

function normalizeEvent(event: FrontendLogEvent): FrontendLogEvent {
    const runtime = getBrowserRuntime();

    return {
        level: event.level ?? "Information",
        category: event.category,
        eventName: event.eventName,
        componentOrWidget: event.componentOrWidget,
        path: event.path ?? (runtime ? getDefaultPath(runtime) : undefined),
        url: event.url ?? runtime?.location?.href,
        sessionId: event.sessionId,
        requestId: event.requestId,
        data: event.data,
    };
}

function isErrorLevel(level?: string): boolean {
    if (!level) {
        return false;
    }

    switch (level.trim().toLowerCase()) {
        case "error":
        case "fatal":
        case "critical":
            return true;
        default:
            return false;
    }
}

function clearFlushTimer() {
    if (flushTimer === null) {
        return;
    }

    clearTimeout(flushTimer);
    flushTimer = null;
}

function scheduleFlush() {
    const runtime = getBrowserRuntime();
    if (!runtime || flushTimer !== null) {
        return;
    }

    flushTimer = runtime.setTimeout(() => {
        flushTimer = null;
        void flushQueue();
    }, flushIntervalMs);
}

function bindLifecycleEvents() {
    const runtime = getBrowserRuntime();
    if (!runtime || hasBoundLifecycleEvents) {
        return;
    }

    const flushOnPageExit = () => {
        void flushQueue();
    };

    runtime.addEventListener("pagehide", flushOnPageExit);
    runtime.addEventListener("beforeunload", flushOnPageExit);
    hasBoundLifecycleEvents = true;
}

async function flushQueue(): Promise<void> {
    const runtime = getBrowserRuntime();
    if (!runtime || typeof runtime.fetch !== "function" || queue.length === 0) {
        return;
    }

    clearFlushTimer();

    const events = queue.splice(0, maxBatchSize);

    try {
        await runtime.fetch(batchEndpoint, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            credentials: "same-origin",
            keepalive: true,
            body: JSON.stringify({ events }),
        });
    } catch {
        // Swallow logging failures to avoid impacting user flows.
    }

    if (queue.length > 0) {
        scheduleFlush();
    }
}

function logEvent(event: FrontendLogEvent) {
    if (!getBrowserRuntime() || !isErrorLevel(event.level)) {
        return;
    }

    bindLifecycleEvents();
    queue.push(normalizeEvent(event));

    if (queue.length >= maxBatchSize) {
        void flushQueue();
        return;
    }

    scheduleFlush();
}

function logError(event: FrontendLogEvent) {
    logEvent({
        ...event,
        level: event.level ?? "Error",
    });
}

export const frontendLogger: FrontendLoggerApi = {
    logEvent,
    logError,
    flush: flushQueue,
};

export default frontendLogger;